import { generateStyledQrCode } from '@/api/qrCode';
import type { CanvasEx } from '@/typings/canvas';
import type {
    QrConfig,
    QrCornersDotType,
    QrCornersSquareType,
    QrDotsType,
    QrLegacyConfig,
    QrShape,
    QrStyleOptions,
    QrStyledConfig,
    VectorPlaceholderElement
} from '@/typings/customilyElements/canvasElements';

export const TRANSPARENT_COLOR = 'transparent';

export const DEFAULT_QR_DATA = 'https://www.customily.com/';

export const DEFAULT_QR_STYLE_OPTIONS: QrStyleOptions = {
    data: DEFAULT_QR_DATA,
    shape: 'square',
    margin: 0,
    dotsOptions: { type: 'square', color: '#000000' },
    cornersSquareOptions: { type: 'square', color: '#000000' },
    cornersDotOptions: { type: 'square', color: '#000000' },
    backgroundOptions: { round: 0, color: TRANSPARENT_COLOR }
};

export const QR_SHAPES: { label: string; value: QrShape }[] = [
    { label: 'Square', value: 'square' },
    { label: 'Circle', value: 'circle' }
];

export const QR_DOTS_TYPES: { label: string; value: QrDotsType }[] = [
    { label: 'Square', value: 'square' },
    { label: 'Dots', value: 'dots' },
    { label: 'Rounded', value: 'rounded' },
    { label: 'Extra rounded', value: 'extra-rounded' },
    { label: 'Classy', value: 'classy' },
    { label: 'Classy rounded', value: 'classy-rounded' }
];

// The corner options accept the same set as the dots plus 'dot' - see the qr-code-generator
//  module of the storefront service.
const CORNER_TYPES = [
    { label: 'Square', value: 'square' },
    { label: 'Dot', value: 'dot' },
    { label: 'Dots', value: 'dots' },
    { label: 'Rounded', value: 'rounded' },
    { label: 'Extra rounded', value: 'extra-rounded' },
    { label: 'Classy', value: 'classy' },
    { label: 'Classy rounded', value: 'classy-rounded' }
];

export const QR_CORNERS_SQUARE_TYPES = CORNER_TYPES as { label: string; value: QrCornersSquareType }[];

export const QR_CORNERS_DOT_TYPES = CORNER_TYPES as { label: string; value: QrCornersDotType }[];

/**
 * QR elements created before the styled generator store `{ text, pixelSize, lightColor,
 *  darkColor }` and keep rendering through the legacy endpoint. The styled ones are the only
 *  ones carrying `options`.
 */
export const isStyledQrConfig = (config?: QrConfig | string | null): config is QrStyledConfig => {
    if (!config || typeof config === 'string') return false;
    return !!(config as QrStyledConfig).options;
};

export const buildStyledQrConfig = (options: Partial<QrStyleOptions> = {}): QrStyledConfig => ({
    options: {
        ...DEFAULT_QR_STYLE_OPTIONS,
        ...options,
        dotsOptions: { ...DEFAULT_QR_STYLE_OPTIONS.dotsOptions, ...options.dotsOptions },
        cornersSquareOptions: {
            ...DEFAULT_QR_STYLE_OPTIONS.cornersSquareOptions,
            ...options.cornersSquareOptions
        },
        cornersDotOptions: { ...DEFAULT_QR_STYLE_OPTIONS.cornersDotOptions, ...options.cornersDotOptions },
        backgroundOptions: { ...DEFAULT_QR_STYLE_OPTIONS.backgroundOptions, ...options.backgroundOptions }
    }
});

export const getQrData = (config?: QrConfig): string => {
    if (isStyledQrConfig(config)) return config.options.data;
    return (config as QrLegacyConfig | undefined)?.text || '';
};

/** Sets the encoded value on either config shape, without mutating the original. */
export const setQrData = (config: QrConfig, data: string): QrConfig => {
    if (isStyledQrConfig(config)) {
        return { ...config, options: { ...config.options, data } };
    }

    return { ...(config as QrLegacyConfig), text: data };
};

// Style changes fire one request each; a slow response must never overwrite the result of a
//  newer one, so every element keeps the token of its latest request.
const renderTokens = new WeakMap<VectorPlaceholderElement, number>();

export const renderStyledQr = async (
    element: VectorPlaceholderElement,
    canvas: CanvasEx,
    options?: QrStyleOptions
): Promise<void> => {
    const qrOptions = options || (isStyledQrConfig(element.qrConfig) ? element.qrConfig.options : undefined);
    if (!qrOptions) return;

    const token = (renderTokens.get(element) || 0) + 1;
    renderTokens.set(element, token);

    const svgString = await generateStyledQrCode(qrOptions);
    if (renderTokens.get(element) !== token) return;

    element.set('svgString', svgString);
    // The control rectangle/init position are reapplied asynchronously once the SVG finishes
    //  loading (see fabric.VectorPlaceholder._set) - render before this resolves and the canvas
    //  paints the placeholder mid-reset instead of its saved position.
    await element._contentLoadPromise;
    canvas.requestRenderAll();
};

/** Persists a style change on the element and repaints it with the new QR. */
export const updateStyledQrOptions = async (
    element: VectorPlaceholderElement | undefined,
    canvas: CanvasEx,
    options: Partial<QrStyleOptions>,
    editMode = false
): Promise<void> => {
    if (!element || !isStyledQrConfig(element.qrConfig)) return;

    const newConfig = buildStyledQrConfig({ ...element.qrConfig.options, ...options });
    element.set('qrConfig', newConfig);
    if (editMode) element.setEditedProp('qrConfig');

    await renderStyledQr(element, canvas, newConfig.options);
};
